import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { formatLongDisplayDate, normalizeApiDate } from "./dateFormatter";

export const CALENDAR_CELL_WIDTH = 74;
export const CALENDAR_UNIT_COLUMN_WIDTH = 240;
export const CALENDAR_ROW_HEIGHT = 74;

export const CALENDAR_UNIT_ACCENTS = [
  "#14b8a6",
  "#8b5cf6",
  "#22c55e",
  "#f97316",
  "#2563eb",
  "#ec4899",
  "#06b6d4",
];

export type CalendarMonthSegment = {
  key: string;
  label: string;
  startIndex: number;
  span: number;
};

export type CalendarAvailablePeriod = {
  start: string;
  end: string;
  nights: number;
};

export const toCalendarApiDate = (value: string): string => {
  return normalizeApiDate(value);
};

export const formatCalendarRange = (from: string, until: string): string => {
  return `${formatLongDisplayDate(from)} - ${formatLongDisplayDate(until)}`;
};

export const formatCalendarMonthLabel = (date: string): string => {
  const parsedDate = new Date(`${normalizeApiDate(date)}T00:00:00`);

  return parsedDate
    .toLocaleDateString("bs-BA", {
      month: "long",
      year: "numeric",
    })
    .toUpperCase();
};

export const formatCalendarDayName = (date: string): string => {
  const parsedDate = new Date(`${normalizeApiDate(date)}T00:00:00`);

  return parsedDate
    .toLocaleDateString("bs-BA", {
      weekday: "short",
    })
    .replace(".", "")
    .toUpperCase();
};

export const getCalendarDayNumber = (date: string): string => {
  const parsedDate = new Date(`${normalizeApiDate(date)}T00:00:00`);

  return String(parsedDate.getDate()).padStart(2, "0");
};

export const getCalendarMonthNumber = (date: string): string => {
  const parsedDate = new Date(`${normalizeApiDate(date)}T00:00:00`);

  return String(parsedDate.getMonth() + 1).padStart(2, "0");
};

export const isCalendarWeekend = (date: string): boolean => {
  const parsedDate = new Date(`${normalizeApiDate(date)}T00:00:00`);
  const day = parsedDate.getDay();

  return day === 0 || day === 6;
};

export const isCalendarToday = (date: string): boolean => {
  return normalizeApiDate(date) === new Date().toISOString().split("T")[0];
};

export const getCalendarMonthSegments = (
  dates: string[],
): CalendarMonthSegment[] => {
  const segments: CalendarMonthSegment[] = [];

  dates.forEach((date, index) => {
    const key = normalizeApiDate(date).slice(0, 7);
    const currentSegment = segments[segments.length - 1];

    if (currentSegment?.key === key) {
      currentSegment.span += 1;
      return;
    }

    segments.push({
      key,
      label: formatCalendarMonthLabel(date),
      startIndex: index,
      span: 1,
    });
  });

  return segments;
};

export const getCalendarUnitAccent = (
  unit: UnitResponse,
  index: number,
): string => {
  return CALENDAR_UNIT_ACCENTS[
    (unit.id + index) % CALENDAR_UNIT_ACCENTS.length
  ];
};

export const hexToRgba = (hexColor: string, opacity: number): string => {
  const normalizedHex = hexColor.replace("#", "");

  if (!/^[0-9A-Fa-f]{6}$/.test(normalizedHex)) {
    return "rgba(37, 99, 235, 0.10)";
  }

  const red = parseInt(normalizedHex.slice(0, 2), 16);
  const green = parseInt(normalizedHex.slice(2, 4), 16);
  const blue = parseInt(normalizedHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

export const getVisibleBookingBarStyle = (
  booking: BookingResponse,
  dates: string[],
): { left: number; width: number } | null => {
  if (dates.length === 0) {
    return null;
  }

  const checkIn = toCalendarApiDate(booking.check_in);
  const checkOut = toCalendarApiDate(booking.check_out);

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  if (checkOut < firstDate || checkIn > lastDate) {
    return null;
  }

  const visibleStart = checkIn < firstDate ? firstDate : checkIn;
  const visibleEnd = checkOut > lastDate ? lastDate : checkOut;

  const startIndex = dates.findIndex((date) => date === visibleStart);
  const endIndex = dates.findIndex((date) => date === visibleEnd);

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  const startsBeforeVisibleRange = checkIn < firstDate;
  const endsAfterVisibleRange = checkOut > lastDate;

  const left =
    CALENDAR_UNIT_COLUMN_WIDTH +
    startIndex * CALENDAR_CELL_WIDTH +
    (startsBeforeVisibleRange ? 8 : CALENDAR_CELL_WIDTH / 2);

  const right =
    CALENDAR_UNIT_COLUMN_WIDTH +
    endIndex * CALENDAR_CELL_WIDTH +
    (endsAfterVisibleRange ? CALENDAR_CELL_WIDTH - 8 : CALENDAR_CELL_WIDTH / 2);

  return {
    left,
    width: Math.max(right - left, 56),
  };
};

export const getDaysBetweenCalendarDates = (
  from: string,
  until: string,
): number => {
  const start = new Date(`${normalizeApiDate(from)}T00:00:00`);
  const end = new Date(`${normalizeApiDate(until)}T00:00:00`);

  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
};

export const getAvailablePeriodsForUnit = (
  unit: UnitResponse,
  bookings: BookingResponse[],
  from: string,
  until: string,
): CalendarAvailablePeriod[] => {
  const rangeStart = normalizeApiDate(from);
  const rangeEnd = normalizeApiDate(until);

  const busyPeriods = bookings
    .filter((booking) => {
      return (
        booking.unit_id === unit.id &&
        booking.status !== "cancelled" &&
        toCalendarApiDate(booking.check_out) > rangeStart &&
        toCalendarApiDate(booking.check_in) < rangeEnd
      );
    })
    .map((booking) => ({
      start:
        toCalendarApiDate(booking.check_in) < rangeStart
          ? rangeStart
          : toCalendarApiDate(booking.check_in),
      end:
        toCalendarApiDate(booking.check_out) > rangeEnd
          ? rangeEnd
          : toCalendarApiDate(booking.check_out),
    }))
    .sort((first, second) => first.start.localeCompare(second.start));

  const mergedBusyPeriods: { start: string; end: string }[] = [];

  busyPeriods.forEach((period) => {
    const lastPeriod = mergedBusyPeriods[mergedBusyPeriods.length - 1];

    if (!lastPeriod || period.start > lastPeriod.end) {
      mergedBusyPeriods.push({ ...period });
      return;
    }

    if (period.end > lastPeriod.end) {
      lastPeriod.end = period.end;
    }
  });

  const availablePeriods: CalendarAvailablePeriod[] = [];
  let cursor = rangeStart;

  mergedBusyPeriods.forEach((busyPeriod) => {
    if (cursor < busyPeriod.start) {
      availablePeriods.push({
        start: cursor,
        end: busyPeriod.start,
        nights: getDaysBetweenCalendarDates(cursor, busyPeriod.start),
      });
    }

    if (busyPeriod.end > cursor) {
      cursor = busyPeriod.end;
    }
  });

  if (cursor < rangeEnd) {
    availablePeriods.push({
      start: cursor,
      end: rangeEnd,
      nights: getDaysBetweenCalendarDates(cursor, rangeEnd),
    });
  }

  return availablePeriods.filter((period) => period.nights > 0);
};
